import type { FC } from 'react'

import { twMerge } from 'tailwind-merge'

import { useAppContext } from '@/App.context'

import { FormattedNumberWithCurrency } from '../FormattedNumberWithCurrency'
import { Translation } from '../i18n/Translation'
import { LocalDate } from '../LocalDate'
import { statusStyleByWhiteLabel } from '../util/style-constant'

import type { BuyoutOffering } from './types'

type BuyOutOfferingStatusProps = {
  data: BuyoutOffering
}

export const BuyOutOfferingStatus: FC<BuyOutOfferingStatusProps> = ({ data }) => {
  const { whiteLabel } = useAppContext()

  const isAccepted = data.status === 'ACCEPTED'
  const isRejectedOrExpired = data.status === 'REJECTED' || data.status === 'EXPIRED'
  const statusColor = statusStyleByWhiteLabel[whiteLabel] || statusStyleByWhiteLabel.skytech

  if (!isAccepted && !isRejectedOrExpired) {
    return null
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute inset-0 flex items-center transition-opacity duration-300 group-hover:opacity-0">
          <div
            className={twMerge(
              'h-0.5 w-full opacity-70',
              isAccepted ? statusColor?.success.background : statusColor?.error.background
            )}
          />
        </div>
        <span
          className={twMerge(
            'relative z-10 rounded-lg bg-white px-4 py-2 text-lg font-bold uppercase tracking-wide shadow-md transition-opacity duration-300 group-hover:opacity-0',
            isAccepted ? statusColor?.success.text : statusColor?.error.text
          )}
        >
          <Translation id="Offer" /> <Translation id="{status}" status={data.status} />
        </span>
      </div>

      <div className="flex flex-col gap-2 pt-2 text-black transition-opacity duration-300 group-hover:opacity-100">
        <span className="text-base font-medium">
          <Translation
            id="Buyout price: {amount}"
            amount={
              <span className="text-primary">
                <FormattedNumberWithCurrency amount={data.buyoutPrice} currency={data.currency} />
              </span>
            }
          />
        </span>
        <span className="text-base font-medium">
          {isAccepted ? (
            <Translation
              id="Offer accepted: {dateAndTime}"
              dateAndTime={
                <span className="text-primary">
                  <LocalDate date={data.offerAcceptedDatetime} />
                </span>
              }
            />
          ) : (
            <Translation
              id="Offer declined: {dateAndTime}"
              dateAndTime={
                <span className="text-primary">
                  <LocalDate date={data.offerExpirationDate} />
                </span>
              }
            />
          )}
        </span>
      </div>
    </div>
  )
}
